import React, { useContext, useState } from "react";
import { Button } from "@mui/material";

import { LoadingButton } from "ui";

import { AsyncOperationState } from "fond/async/redux";
import { MapContext } from "fond/map/MapProvider";
import { addNotification } from "fond/notifications/redux";
import { PolygonState } from "fond/project/polygon/redux";
import { getUploadStatus } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal } from "fond/widgets";

import { BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";

import { getBaseDataLayerGroupId } from "./layerGroups";
import MapModeBanner from "./MapModeBanner";

interface DrawModeBarProps {
  drawingKey: BaseDataLayerKey;
  onCancel(): void;
  onDone(): void | Promise<void>;
}

/**
 * Floating map bar shown while a base data layer is drawn, with a hint along the bottom of the map.
 */
const DrawModeBar: React.FC<DrawModeBarProps> = ({ drawingKey, onCancel, onDone }) => {
  const dispatch = useAppDispatch();
  const { isDirty } = useContext(MapContext);
  const isBoundary = drawingKey === "boundary";
  const layerGroupId = getBaseDataLayerGroupId(drawingKey);
  const polygonState = useAppSelector((state) => state.project.polygon.polygonState);
  const uploadStatus = useAppSelector((state) => (layerGroupId ? getUploadStatus(state.project, layerGroupId) : null));
  const [isFinishing, setIsFinishing] = useState(false);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  const isSaving = isFinishing || uploadStatus === AsyncOperationState.executing;
  const title = isBoundary ? "Drawing boundary" : `Drawing ${BASE_DATA_LAYERS[drawingKey].name.toLowerCase()}`;
  const hint = isBoundary ? "Click to add points. Double-click to finish." : "Add or edit features on the map.";

  const handleCancel = () => {
    if (!isBoundary && isDirty) {
      setIsConfirmingDiscard(true);
    } else {
      onCancel();
    }
  };

  const handleDone = async () => {
    setIsFinishing(true);
    try {
      await onDone();
    } catch {
      dispatch(addNotification("There was a problem saving your changes. Please try again.", { variant: "error" }));
    } finally {
      setIsFinishing(false);
    }
  };

  return (
    <>
      <MapModeBanner title={title} hint={hint}>
        <Button size="small" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleDone}
          loading={isSaving}
          disabled={isBoundary && polygonState !== PolygonState.complete}
        >
          Done
        </LoadingButton>
      </MapModeBanner>
      <ConfirmModal
        open={isConfirmingDiscard}
        content="Are you sure you want to discard your changes?"
        onConfirm={() => {
          setIsConfirmingDiscard(false);
          onCancel();
        }}
        onCancel={() => setIsConfirmingDiscard(false)}
      />
    </>
  );
};

export default DrawModeBar;
